import './permissions.css';
import {
  getPermissionMatrix,
  PermissionMatrixApiError,
  type PermissionMatrixRole,
} from '@urbanik/api-client';
import { StatePanel, canonicalStates } from '@urbanik/ui';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
function canonical(kind: 'loading' | 'empty' | 'error' | 'forbidden') {
  return canonicalStates.find((state) => state.kind === kind)!;
}
type ViewState = 'loading' | 'empty' | 'error' | 'forbidden' | 'unauthorized' | 'success';
export function PermissionMatrixView() {
  const forced = new URLSearchParams(window.location.hash.split('?')[1]).get(
    'state',
  ) as ViewState | null;
  const [state, setState] = useState<ViewState>('loading');
  const [roles, setRoles] = useState<readonly PermissionMatrixRole[]>([]);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (forced && forced !== 'success') {
      setState(forced);
      return;
    }
    const controller = new AbortController();
    setState('loading');
    getPermissionMatrix(apiBase, controller.signal)
      .then((result) => {
        setRoles(result.roles);
        setState(result.roles.length ? 'success' : 'empty');
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error instanceof PermissionMatrixApiError)
          setState(
            error.kind === 'forbidden'
              ? 'forbidden'
              : error.kind === 'unauthorized'
                ? 'unauthorized'
                : 'error',
          );
        else setState('error');
      });
    return () => controller.abort();
  }, [attempt, forced]);
  useEffect(() => {
    if (state === 'unauthorized') window.location.hash = 'login';
  }, [state]);
  if (state === 'unauthorized') return null;
  const panelState = canonical(state as 'loading' | 'empty' | 'error' | 'forbidden');
  return (
    <div className="shell-page permissions-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M02.4 · Autorización API</p>
          <h1>Matriz de permisos.</h1>
          <p>
            Diagnóstico de solo lectura calculado desde asignaciones persistidas y autorizado por el
            servidor.
          </p>
        </div>
        <span className="surface-badge">Servidor obligatorio</span>
      </header>
      {state !== 'success' ? (
        <StatePanel state={panelState} onAction={() => setAttempt((v) => v + 1)} />
      ) : (
        <section className="permissions-card" aria-labelledby="matrix-title">
          <div className="permissions-card__heading">
            <div>
              <p className="kicker">Acceso administrativo</p>
              <h2 id="matrix-title">Roles y permisos efectivos</h2>
            </div>
            <strong>{roles.length} roles</strong>
          </div>
          <div className="permissions-table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Rol</th>
                  <th scope="col">Usuarios</th>
                  <th scope="col">Permisos</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role.roleId}>
                    <th scope="row">
                      <strong>{role.roleName}</strong>
                      <code>{role.roleCode}</code>
                    </th>
                    <td>{role.userCount}</td>
                    <td>
                      {role.permissions.length ? (
                        <ul>
                          {role.permissions.map((permission) => (
                            <li key={permission}>
                              <code>{permission}</code>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span>Sin permisos asignados</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
